import ee from './EventDriver';
import FirebaseInterface from './FirebaseInterface';

import UmiStorage from './UmiStorage';
import MultiLangSupport from './MultiLangSupport';
import InterAppCom from './InterAppCom';
import Logger from './Logger';

import Utils from './utils/Utils';
import DateUtils from './utils/DateUtils';

/**
 * UmiCore
 */
class UmiCore {

  constructor() {
    this.settings = this.getSettings();
    this.interAppCom = new InterAppCom(name); // Global "name" param
    window.Landbot = this.interAppCom;  // Global usage
    this.interAppCom.send('landbot-init');
    this.fi = new FirebaseInterface();
    this.umiStorage = new UmiStorage(this.settings.storage_off);
    this.multiLang = new MultiLangSupport();
    Logger.append(
      `Multilanguage support [${this.multiLang.lang}]`,
      'umicore.multilang'
    );

    this.settings.serverUrl = this.settings.serverUrl || 'https://app.landbot.io/';
    this.settings.messageLimit = this.settings.messageLimit || 20;
    this.metaDataInfo = this.getMetadataInfo();
    this.provisionalWelcome = null;
    this.tempInitialMessages = [];
    this.welcomeLaunched = false;
    this.firstMessageDate = Date.now();
    this.agentsInfo = this.getStoredAgents();
    if (!this.agentsInfo['brand'])
      this.agentsInfo['brand'] = {
        name: this.settings.brand_name || 'landbot',
        avatar: this.settings.logo || 'https://storage.googleapis.com/media.helloumi.com/brands/helloumi.png',
      };

    this.setupListeners();
    this.ready();
  }

  getSettings() {
    const settings = UmiCoreConfig || {};
    // Obtain Queries
    const queries = {};
    const params = window.location.search
      .substring(1)
      .split('&');

    let pair = null;
    for (let i = 0; i < params.length; i++) {
      pair = params[i].split('=');
      queries[pair[0]] = pair[1];
    }
    return Object.assign(settings, queries);
  }

  extendSettings(external) {
    const settings = JSON.parse(JSON.stringify(this.settings));
    this.settings = Object.assign(
      settings,
      external
    );
  }

  setupListeners() {
    // InterAppCom
    ee.on('hu-start-connection', () => this.interAppCom.send('landbot-load'));
    ee.on('hu-receive-message', (message) => this.interAppCom.send('landbot-msg-receive', message));
    this.interAppCom.on('landbot-config', (config) => this.extendSettings(config));
    this.interAppCom.on('landbot-msg-send', (messageInfo) => {
      this.sendMessage(messageInfo);
    });
    this.interAppCom.on('landbot-custom-data', (customData) => {
      this.settings.customData = Object.assign(
        this.settings.customData ? { customData: this.settings.customData } : {},
        customData,
      );
    });

    // Events Emitter
    ee.on('hu-query-send-message', (messageInfo) => this.sendMessage(messageInfo));
    ee.on('hu-start-connection', () => this.startConnnection());
    // ee.on('hu-database-auth', (e) => this.authProvider(e));
    // ee.on('hu-auth', () => this.loadConfig());
    // ee.on('hu-load-config', () => this.ready());
    // ee.on('hu-fi-broadcast-message', (listmessage) => this.broadCastMessage(listmessage));
    // ee.on('hu-customer-identify', (initial) => this.startMessage(initial));
    ee.on('hu-sent-message', (data) => this.checkCustomerToken(data));
    ee.on('hu-query-old-messages',() => this.oldChats());
    ee.on('hu-query-stored-agents', () => this.sendStoredAgents());
    ee.on('hu-query-get-agent', (agentID) => this.fetchAgentData(agentID));
    ee.on('hu-receive-last-group-message', (message) => this.interAppCom.send('landbot-proactive-msg-receive', message));
    ee.on('hu-receive-last-brand-message', (message) => this.interAppCom.send('landbot-last-brand-msg-receive', message));
  }

  fetchAgentData(agentID) {
    if (agentID < 1)
      return;
    if (this.agentsInfo[agentID]) {
      return;
    }
    this.agentsInfo[agentID] = {};
    this.fi.getAgentData(agentID)
      .then((agentData) => {
        Logger
          .append(
            `${DateUtils.format(new Date(), 'HH:mm:ss.ff')}: Agent info received`,
            `umicore.agents[${agentID}]`
          )
          .append(
            agentData,
            `umicore.agents[${agentID}]`
          );
        ee.emit('hu-get-agent', {
          name: agentData.name,
          avatar: agentData.avatar,
          samurai: agentID,
        });
        this.agentsInfo[agentID] = {
          name: agentData.name,
          avatar: agentData.avatar,
        };
        this.interAppCom.send('landbot-proactive-agents-info', this.agentsInfo);
        this.storeAgents();
      })
      .catch(() => {
        delete this.agentsInfo[agentID];
      });
  }

  sendStoredAgents() {
    ee.emit('hu-get-stored-agents', this.agentsInfo);
    this.interAppCom.send('landbot-proactive-agents-info', this.agentsInfo);
    Logger
      .append(
        `${DateUtils.format(new Date(), 'HH:mm:ss.ff')}: Got stored agents`,
        `umicore.agents.info`
      )
      .append(
        this.agentsInfo,
        `umicore.agents.info`
      );
    // console.debug('%cUmicore debug: Got stored agents', 'color:green');
    // console.debug(this.agentsInfo);
  }

  getStoredAgents() {
    return JSON.parse(
      this.umiStorage.getItem(`agents-info-${this.settings.channelToken}`) || '{}'
    );
  }

  storeAgents() {
    this.umiStorage.setItem(`agents-info-${this.settings.channelToken}`, JSON.stringify(this.agentsInfo));
  }
  
  oldChats() {
    this.fi.getOldChats()
      .then((messages) => {
        messages = this.fixTimestamp(messages);
        ee.emit('hu-old-chat', messages);
        // console.debug(messages);
      })
      .catch()
  }

  authProvider() {
    this.fi.auth()
      .then((user) => {
        if (user !== undefined) {
          ee.emit('hu-auth');
        }
      })
      .catch((err) => {
        ee.emit('hu-error-auth');
      });
  }

  loadConfig() {
    ee.emit('hu-load-config', this.settings);
  }

  ready() {
    ee.emit('hu-umicore-ready', 'hu-umicore-ready');
  }

  broadCastMessage(listmessage) {
    // TODO
  }

  randomId() {
    return `H-${this.settings.brandID}${Utils.randomKey(10)}`;
  }

  getRandomWelcome() {
    const defaultMessages = [
      {
        'type': 'dialog',
        'title': 'Hello there!',
        'buttons': [ 'Hi!' ],
        'payloads': [ 'Hi!' ],
        'samurai': '-1',
      },
      {
        'type': 'dialog',
        'title': 'Howdy!',
        'buttons': [ 'Hello!' ],
        'payloads': [ 'Hello!' ],
        'samurai': '-1',
      },
      {
        'type': 'dialog',
        'title': 'Welcome!',
        'buttons': [ 'Howdy!' ],
        'payloads': [ 'Howdy!' ],
        'samurai': '-1',
      }
    ];
    return [defaultMessages[Math.floor(Math.random()*defaultMessages.length)]];
  }

  getWelcomeMessages(ignoreStorage = false) {
    if (this.umiStorage.hasOwnProperty(`welcomeMessage-${this.settings.channelToken}`) && !ignoreStorage && !this.settings.test) {
      // console.debug('%cUmiCore debug: Welcome messages obtained from local Storage', 'color:green');
      Logger.append(
        `Welcome messages obtained from local Storage`,
        'umicore.welcome'
      );
      return JSON.parse(this.umiStorage.getItem(`welcomeMessage-${this.settings.channelToken}`));
    }

    let welcome = this.settings.welcome || [];
    welcome = (welcome.length > 0) ? welcome : this.getRandomWelcome();
    // console.debug('%cUmiCore debug: Welcome message', 'color:green');
    // console.debug(welcome);
    Logger.append('Welcome message', 'umicore.welcome');
    Logger.append(welcome, 'umicore.welcome');

    let messagesList = welcome;
    if (messagesList.hasOwnProperty('messages'))
      messagesList = Object.values(messagesList.messages); 

    let now = Date.now();
    if (messagesList.length === 0) {
      return [];
    }
    messagesList.forEach((message, counter) => {
      message.timestamp = counter + now;
      message.key = Utils.randomKey();
      message.role = message.role ? message.role.concat('welcome') : ['welcome'];
    });
    return messagesList;
  }

  welcomeMessages() {
    return new Promise((resolve,reject) => {
      let messagesList = this.getWelcomeMessages();
      messagesList.forEach((message) => {
        ee.emit('hu-receive-message', message);
      });
      this.provisionalWelcome = messagesList;
      // console.debug('%cUmiCore debug: provisional welcome is:', 'color:green');
      // console.debug(this.provisionalWelcome);
      this.welcomeLaunched = true;
      resolve(true);
    });
  }

  storeWelcomeMessage() {
    // console.debug('%cUmiCore debug: Welcome messages stored in local Storage', 'color:green');
    this.umiStorage.removeItem(`welcomeMessage-${this.settings.channelToken}`);
    this.umiStorage.setItem(`welcomeMessage-${this.settings.channelToken}`, JSON.stringify(this.provisionalWelcome));
    this.provisionalWelcome = null;
  }

  getRandomRevisit() {
    return this.multiLang.getObject('revisit');
  }

  getRevisitMessages() {
    let revisit = this.settings.revisit || [];
    revisit = (revisit.length > 0) ? revisit : this.getRandomRevisit();
    // console.debug('%cUmiCore debug: Revisit message', 'color:green');
    // console.debug(revisit);
    Logger.append(
      revisit,
      'umicore.revisit'
    );

    let messagesList = revisit;

    let now = Date.now();
    if (messagesList.length === 0) {
      return [];
    }
    messagesList.forEach((message, counter) => {
      message.timestamp = counter + now;
      message.key = Utils.randomKey();
      message.role = message.role ? message.role.concat('revisit') : ['revisit'];
    });
    return messagesList;
  }

  revisitMessages() {
    return new Promise((resolve, reject) => {
      const messagesList = this.getRevisitMessages();
      messagesList.forEach((message) => {
        ee.emit('hu-receive-message', message);
      });
      // console.debug('%cUmiCore debug: Launched revisit messages', 'color:green');
      resolve(true);
    });
  }

  fixTimestamp(listMessage) {
    listMessage.forEach((message) => {
      message.timestamp *= 1000;
    });
    return listMessage;
  }

  getAllMessages(initial = false) {
    ee.emit('hu-fetching-data');
    return new Promise((resolve, reject) => {
      this.fi.getAllMessages(this.settings.messageLimit)
        .then((messages) => {
          messages = this.fixTimestamp(messages);
          if (
            (messages.length < this.settings.messageLimit) && 
            !this.welcomeLaunched
          ) {
            // console.debug('%cUmiCore debug: Messages loaded are less than the limit. Concatenating welcome messages...', 'color;');
            Logger.append(
              'Messages loaded are less than the limit. Concatenating welcome messages...',
              'umicore.welcome'
            );
            const welcome = this.getWelcomeMessages();
            // let timestamp = messages[0].timestamp - welcome.length;
            messages = welcome.concat(messages);
          }

          if (!initial) { // Nueva conversación
            messages.forEach((message) => {
              if (message.samurai !== undefined)
                ee.emit('hu-receive-message', message);
            });
          } else  // Conversación iniciada previamente
            ee.emit('hu-initial-chat', messages);

          // console.debug('%cUmiCore debug: messages loaded successfuly from firebase', 'color:blue');
          // console.debug(messages);
          Logger.append(
            messages,
            'umicore.messages'
          );

          resolve();
        })
        .catch((error) => {
          // No es que haya errores, sino que no hay mensajes
          // console.debug('%cUmiCore debug: could not load messages from firebase', 'color:red');
          // console.debug(error);
          Logger.append(
            'Could not load messages from firebase',
            'umicore.messages',
            'error'
          );
          // ee.emit('hu-initial-chat', this.getWelcomeMessages(true));

          reject();
        });
    }); 
  }

  /**
   * setReceiveListener
   * @param {Boolean} newCustomer 
   *    If true, firebase will retrieve all incoming messages, with no filter. 
   *    This is true when it's called from a first user interaction (welcome).
   */
  setReceiveListener(newCustomer) {
    this.fi.setEventListener('addMessage', this.callbackMessageReceive.bind(this));
    this.fi.startConnection(newCustomer);
  }

  callbackMessageReceive(message) {
    message = this.fixTimestamp([message]);
    ee.emit('hu-receive-message', message[0]);

    // Fetch agent data
    if (message[0].samurai && !this.agentsInfo[message[0].samurai])
      this.fetchAgentData(message[0].samurai);

    // Confirm welcome messages
    if (this.provisionalWelcome !== null)
      this.storeWelcomeMessage();
  }

  customerIdentify() {
    return new Promise((resolve, reject) => {
      this.fi.customerIdentify(this.settings.customerToken)
        .then((data) => {
          // console.debug('%cUmiCore debug: customer identified', 'color:green');
          Logger.append(
            `Customer identified [${this.settings.customerToken}]`,
            'umicore.auth'
          );
          this.umiStorage.setItem(`umicore-customer-${this.settings.channelToken}`, this.settings.customerToken);
          ee.emit('hu-customer-identify');
          resolve();
        })
        .catch((error) => {
          // console.debug('%cUmiCore debug: customer could not be identified', 'color:red');
          Logger.append(
            `Customer could not be identified <${this.settings.customerToken}>`,
            'umicore.auth',
            'error'
          );
          this.umiStorage.removeItem(`umicore-customer-${this.settings.channelToken}`);
          this.settings.customerToken = null;

          if (error.code === 1)
            ee.emit('hu-error-wrong-entity', error);
          else if (error.code === 2)
            ee.emit('hu-error-wrong-customer', error);
          else if (error.code === 3)
            ee.emit('hu-error-wrong-channel', error);
          else if (error.code === 0)
            ee.emit('hu-error-server-connection', error);
          else if (error.code === -1)
            ee.emit('hu-error-auth-connection', error);
          reject();
        });
    });    
  }

  /**
   * Start connection steps:
   * – Checks if customer token is defined in config or storage
   * – Then, launch messenger service:
   *    · Welcome messages when no customer token found
   *    · Revisit messages when customer token found
   *    · Normal start, loads previous messages.
   */
  startConnnection() {
    // this.fi.startbroadCastMessage();
    if (!this.settings.customerToken)
      this.settings.customerToken = (this.settings.channelToken) ? 
        this.umiStorage.getItem(`umicore-customer-${this.settings.channelToken}`) : 
        null;
    // console.debug(`%cUmiCore debug: customerToken => ${this.settings.customerToken}`, 'color:green');
    
    // Welcome start
    if (!this.settings.customerToken) {
      this.welcomeMessages();
      return;

    // Revisit start
    } else if (this.settings.revisit_off !== true) {
      this.umiStorage.removeItem(`umicore-customer-${this.settings.channelToken}`);
      this.umiStorage.setItem(`umicore-customer-${this.settings.channelToken}`, this.settings.customerToken);
      this.revisitMessages();
      this.customerIdentify()
        .then(() => this.setReceiveListener())
        // .catch();
      return;

    // Normal start
    } else {
      this.umiStorage.removeItem(`umicore-customer-${this.settings.channelToken}`);
      this.umiStorage.setItem(`umicore-customer-${this.settings.channelToken}`, this.settings.customerToken);
      this.customerIdentify()
        .then(() => {
          this.getAllMessages(true)
            .then(() => this.setReceiveListener())
            .catch(() => this.welcomeMessages());
        })
        // .catch(() => {
        //   this.welcomeMessages()
        // });
      return;
    } 
  }

  getMetadataInfo() {
    return {
      'navigator': {
        'userAgent': navigator.userAgent,
        'languages': navigator.languages,
        'language': navigator.language || navigator.userLanguage,
        'plugins': navigator.plugins,
      },
      'screen': {
        'colorDepth': screen.colorDepth,
        'size': {
          'height': screen.height,
          'width': screen.width,
        },
        'orientation': {
          'type': screen.orientation ? screen.orientation.type : null,
          'angle': screen.orientation ? screen.orientation.angle : null,
        },
        'touchdevice': 'ontouchstart' in window || navigator.maxTouchPoints,
      },
      'location': {
        'origin': Utils.getOrigin(),
        'timezoneOffset': new Date().getTimezoneOffset() / 60,
      },
      // 'os': {
      //   'system': window.gatico,
      // }
    }
  }

  validateInfomessage(message) {  // TODO
    if (
      message.message ||
      (message.images && message.image_flags)
    )
      return true;
    return true; // Provisional
  }

  checkCustomerToken(jData) {
    if (!this.settings.customerToken) // First auth
      ee.emit('hu-first-auth');

    if (
      !this.settings.customerToken || 
      (this.settings.customerToken && (jData.customer_token !== this.settings.customerToken))
    ) {
      this.settings.customerToken = jData.customer_token;
      this.customerIdentify()
        .then(() => this.setReceiveListener(true));
    }
  }

  sendMessage(messageInfo) {
    // Por ahora no hay validación ninguna
    if (!this.validateInfomessage(messageInfo)) {
      ee.emit('hu-error-malformed-message', messageInfo); 
      return;
    }
    // Comprobación de reinicio o continuación del chat inicial
    if (messageInfo.payload === '#_continue') {
      ee.emit('hu-reset-chat');
      this.getAllMessages(true);
        // .then(() => this.setReceiveListener());
      return;
    } else if (messageInfo.payload === '#init') {
      ee.emit('hu-reset-chat');
      messageInfo.message = '#init';
      messageInfo.render = false;
    }

    let [formdata, message] = this.populateFormData(messageInfo);
    if (!this.settings.customerToken) 
      ee.emit('hu-first-message');
    let request = new XMLHttpRequest();
    request.open('POST', this.settings.serverUrl + "webchat/send/");
    request.onload = (oEvent) => {
      let jData = JSON.parse(oEvent.target.response);
      if (request.status == 200 && typeof(jData.errors) === "undefined" ) {

        // console.debug('%cUmiCore debug: message sent successfuly', 'color:green');
        // console.debug(message);
        Logger.append(
          message,
          'umicore.send',
        );
        this.umiStorage.removeItem(`umicore-customer-${this.settings.channelToken}`);
        this.umiStorage.setItem(`umicore-customer-${this.settings.channelToken}`, jData.customer_token);
        ee.emit('hu-sent-message', jData); // Checks customer token in case of updates

      } else {
        // console.debug('%cUmiCore debug: message couldn\'t be sent', 'color:red');
        // console.debug(message);
        Logger.append(
          message,
          'umicore.send',
          'error'
        );
        ee.emit('hu-error-sending-message', oEvent);
      }
    }
    request.onerror = (oEvent) => { ee.emit('hu-error-sending-message', oEvent); };
    request.send(formdata);

    // Este mensaje es renderizado de manera síncrona en la GUI, a diferencia de los recibidos
    ee.emit('hu-sending-message', message);
  }

  populateFormData(messageInfo) {
    let formdata = new FormData();
    let image_flags = [];
    let images = [];
    const message_id = this.randomId();

    formdata.append('message', messageInfo.message);
    formdata.append('payload', messageInfo.payload);
    if (messageInfo.images) {
      for (let i = 0; i < messageInfo.images.length; i++) {
        if (messageInfo.image_flags[i]) {
          formdata.append('images',messageInfo.images[i]);
          images.push(messageInfo.images[i]);
          image_flags.push(true);
        }
      }
    }
    formdata.append('image_flags', image_flags);
    formdata.append('version', this.settings.version);
    formdata.append('channel_token', this.settings.channelToken);
    formdata.append('customer_token', this.settings.customerToken);
    formdata.append('message_id', message_id);
    formdata.append('origin', Utils.getOrigin());
    
    if (this.settings.customData)
      formdata.append('custom_data', JSON.stringify(this.settings.customData));
    if (this.metaDataInfo) {
      formdata.append('metadata_info', JSON.stringify(this.metaDataInfo));
      this.metaDataInfo = null;
    }
    if (this.settings.customerName)
      formdata.append('customer_name', this.settings.customerName);

    messageInfo.message_id = message_id;
    messageInfo.images = images;
    messageInfo.image_flags = image_flags;

    let messageStructure = {
      "message": messageInfo.message,
      "message_id": message_id,
      "render": messageInfo.render,
    };

    if (images.length === 0) {
      messageStructure['type'] = 'text';
    } else if (images.length === 1) {
      messageStructure['type'] = 'image';
      messageStructure['url'] = images[0];
    } else if (images.length > 1) {
      messageStructure['type'] = 'multiple_images';
      messageStructure['urls'] = images; 
    }
    return [formdata, messageStructure];
  }
}

export default UmiCore;



// WEBPACK FOOTER //
// ../UmiCore/src/UmiCore.js